<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaStatusIndicator from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFonts, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /** Render a status dot (e.g. "alert" for load errors) instead of the dashed icon box. */
  status: {
    type: String as PropType<'alert' | 'risk' | 'warning' | 'info' | undefined>,
    default: undefined,
  },
  /**
   * Frame the block in a dashed box and tighten it. Used where the empty state
   * sits inside a step of a wizard (the CM view's column step) rather
   * than filling a page — there the dash reads as a slot waiting to be filled.
   */
  dashed: {
    type: Boolean,
    default: false,
  },
});

const boxBorder = gaiaNeutral.borderDashed;
const iconColor = gaiaNeutral.textMuted;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <!-- Centered empty/no-match/error block from the CM mocks. Naive's n-empty
       can't express the dashed icon box, status dot, mono meta line or the
       action row, so this is self-contained on the shared tokens. -->
  <n-flex
    vertical
    align="center"
    :size="8"
    class="gaia-empty-state"
    :class="{ dashed: props.dashed }"
    :style="{ textAlign: 'center', padding: props.dashed ? '34px 28px 32px' : '52px 40px 48px' }"
  >
    <gaia-status-indicator
      v-if="props.status"
      :status="props.status"
      :style="{ marginBottom: '10px' }"
    />
    <n-flex
      v-else-if="$slots.icon"
      align="center"
      justify="center"
      :size="0"
      :wrap="false"
      class="icon-box"
    >
      <slot name="icon" />
    </n-flex>
    <gaia-text size="subtitle" weight="semibold" tone="ink">{{ props.title }}</gaia-text>
    <gaia-text
      v-if="$slots.default"
      size="body"
      tone="tertiary"
      :style="{ lineHeight: 1.55, maxWidth: '340px' }"
    >
      <slot />
    </gaia-text>
    <gaia-text
      v-if="$slots.meta"
      size="micro"
      tone="muted"
      :style="{ fontFamily: monoFont, marginTop: '2px' }"
    >
      <slot name="meta" />
    </gaia-text>
    <n-flex v-if="$slots.actions" :size="8" :wrap="false" :style="{ marginTop: '12px' }">
      <slot name="actions" />
    </n-flex>
  </n-flex>
</template>

<style scoped>
.gaia-empty-state.dashed {
  border: 1px dashed v-bind(boxBorder);
  border-radius: 12px;
}

/* A fixed dashed square that centres whatever icon is slotted. The size,
   radius and dashed border have no naive equivalent, and font-size here is an
   icon-box metric rather than type, so it stays out of the text scale. */
.icon-box {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px dashed v-bind(boxBorder);
  margin-bottom: 6px;
  color: v-bind(iconColor);
  font-size: 18px;
}
</style>
